// src/rms/pages/CheckinFlow.js
// ============================================================================
// Check-in
// ============================================================================
// Transactional screen — no ad slots, by rule.
//
// Closes the agreement, compares return readings against checkout, applies
// usage overage, resolves the deposit, and raises the invoice. Items that come
// back dirty or damaged go to maintenance rather than straight back on the
// shelf.
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import db from "../data";
import rentalService from "../data/rentalService";
import { useRms } from "../context/RmsContext";
import { PageHeader, Banner, Field, DynamicField, SignaturePad, formatDateTime, assetLabel } from "../components/ui";
import { fieldsCapturedAt } from "../verticals/presets";
import adEngine from "../ads/adEngine";
import PinModal from "../components/PinModal";
import pinService from "../data/pinService";
import agreementDocumentService from "../data/agreementDocumentService";
import { getLocale } from "../i18n/locale";

function CheckinFlow() {
  const { agreementId } = useParams();
  const navigate = useNavigate();
  const { operator, config, t, tLower, formatMoney, workflow, invalidate } = useRms();

  const [agreement, setAgreement] = useState(null);
  const [signedDocuments, setSignedDocuments] = useState([]);
  const [asset, setAsset] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const [captured, setCaptured] = useState({});
  const [damageFound, setDamageFound] = useState(false);
  const [damageNotes, setDamageNotes] = useState("");
  const [damageCharge, setDamageCharge] = useState("");
  const [signature, setSignature] = useState(null);
  const [error, setError] = useState(null);

  // The banner sits at the top of a long form, and the button that triggers it
  // is at the bottom. Without this the refusal renders off-screen and the
  // check-in looks like it simply did nothing.
  const errorRef = useRef(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [error]);
  const [busy, setBusy] = useState(false);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ag = (await db.get("agreements", agreementId)).data;
      if (!ag || cancelled) {
        setLoading(false);
        return;
      }
      const a = (await db.get("assets", ag.asset_id)).data;
      const c = (await db.get("customers", ag.customer_id)).data;
      // By reservation, not by agreement_id: a document signed remotely before
      // the customer arrived is attached to the booking, and only picks up an
      // agreement_id afterwards if a checkout happened at all.
      const docs = ag.reservation_id
        ? await agreementDocumentService.listForReservation(ag.operator_id, ag.reservation_id)
        : { data: [] };
      if (cancelled) return;
      setSignedDocuments((docs.data || []).filter((d) => d.status === "signed"));
      setAgreement(ag);
      setAsset(a);
      setCustomer(c);
      setCaptured({ ...(ag.checkout_data || {}) });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [agreementId]);

  const checkinFields = useMemo(
    () => (config ? fieldsCapturedAt(config.key, "checkin", operator?.config_overrides) : []),
    [config, operator]
  );

  // Live preview of what the customer will be billed, before committing.
  const overagePreview = useMemo(() => {
    if (!agreement || !config) return [];
    const days = Math.max(
      1,
      Math.ceil((Date.now() - new Date(agreement.checkout_at).getTime()) / 864e5)
    );
    return (config.usageMetrics || [])
      .filter((m) => m.billable)
      .map((m) => {
        const out = Number(agreement.checkout_data?.[m.key]);
        const back = Number(captured[m.key]);
        if (!Number.isFinite(out) || !Number.isFinite(back) || back <= out) return null;
        const used = back - out;
        const included = (m.includedPerDay || 0) * days;
        const over = Math.max(0, used - included);
        return { metric: m, used, included, over, cents: Math.round(over * (m.overageCentsPerUnit || 0)) };
      })
      .filter(Boolean);
  }, [agreement, config, captured]);

  if (loading) return <p style={{ color: "var(--rms-muted-strong)" }}>Loading…</p>;
  if (!agreement) {
    return (
      <>
        <PageHeader title={t("checkinFlow.notFound", "Not found")} subtitle={t("checkinFlow.thatAgreementNoLonger", "That agreement no longer exists.")} />
        <Link className="rms-btn rms-btn--ghost" to="/rms">
          {t("checkinFlow.backToDashboard", "Back to dashboard")}
        </Link>
      </>
    );
  }

  const canClose = !workflow.signatureRequiredAtCheckin || !!signature;

  /**
   * Taking the asset back is signed, for the same reason releasing it is --
   * but only once we know the check-in can actually go through.
   *
   * The PIN used to be collected first. That wrote a permanent row to the
   * append-only action log, and then the check-in could still be refused for a
   * missing customer signature, leaving the log saying this agreement was
   * closed while it sat open. Ask the questions that have answers before asking
   * for a signature.
   */
  const handleClose = async () => {
    const pre = await rentalService.validateCheckIn(operator, {
      agreementId,
      signaturePath: signature,
    });
    if (!pre.success) {
      setError(pre.error);
      return;
    }
    setError(null);
    setSigning(true);
  };

  const close = async (pin) => {
    const signed = await pinService.signAction(operator, {
      pin,
      action: "agreement.close",
      subjectTable: "rms_agreements",
      subjectId: agreementId,
      // Damage is what gets disputed later, so the finding is part of the
      // signed record rather than something read off the row afterwards.
      context: { damage_found: !!damageFound },
    });
    if (!signed.success) return { error: signed.error };

    setBusy(true);
    adEngine.recordAction();
    const result = await rentalService.checkIn(operator, {
      agreementId,
      capturedFields: captured,
      signaturePath: signature,
      damageFound,
      damageNotes,
      damageChargeCents: Math.round(Number(damageCharge || 0) * 100),
      signatureId: signed.data,
    });
    setBusy(false);
    setSigning(false);

    if (!result.success) {
      setError(result.error);
      return {};
    }
    invalidate();
    navigate("/rms/billing");
    return {};
  };

  const overageTotal = overagePreview.reduce((s, o) => s + o.cents, 0);
  const damageCents = Math.round(Number(damageCharge || 0) * 100);

  return (
    <>
    <>
      <PageHeader
        title={t("checkin.title", "{action} — {item}", {
          action: t("checkin", "Check in"),
          item: assetLabel(asset),
        })}
        subtitle={`${agreement.agreement_number} · out since ${formatDateTime(agreement.checkout_at)}`}
        actions={
          <Link className="rms-btn rms-btn--ghost" to="/rms">
            {t("action.cancel", "Cancel")}
          </Link>
        }
      />

      {error ? (
        <div ref={errorRef}>
          <Banner tone="danger">{error}</Banner>
        </div>
      ) : null}

      {/* What they signed on the way out. Here because this is the screen
          somebody is on when the argument starts -- the unit is back, there is
          a mark on it, and the question is what was agreed. */}
      {signedDocuments.length ? (
        <div className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
          <h2 className="rms-section-title">{t("checkin.signedTitle", "Signed at checkout")}</h2>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {signedDocuments.map((doc) => (
              <Link
                key={doc.id}
                className="rms-btn rms-btn--ghost rms-btn--sm"
                to={`/rms/agreement/${doc.id}`}
              >
                {doc.title}
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 300px", gap: 22, alignItems: "start" }}>
        <div>
          {checkinFields.length ? (
            <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
              <h2 className="rms-section-title">{t("checkin.readingsTitle", "Readings & condition on return")}</h2>
              <div className="rms-field-grid">
                {checkinFields.map((f) => (
                  <div key={f.key}>
                    <DynamicField
                      field={f}
                      value={captured[f.key]}
                      allValues={captured}
                      onChange={(v) => setCaptured((c) => ({ ...c, [f.key]: v }))}
                    />
                    {agreement.checkout_data?.[f.key] !== undefined && !Array.isArray(agreement.checkout_data[f.key]) ? (
                      <div className="rms-help" style={{ marginTop: -8, marginBottom: 12 }}>
                        {t("checkin.atCheckout", "At {action}:", { action: tLower("checkout", "checkout") })}{" "}
                        <strong>{String(agreement.checkout_data[f.key])}</strong>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
            <h2 className="rms-section-title">{t("checkinFlow.damage", "Damage")}</h2>
            <label className="rms-checkline">
              <input type="checkbox" checked={damageFound} onChange={(e) => setDamageFound(e.target.checked)} />
              {t("checkinFlow.damageFoundOnReturn", "Damage found on return")}
            </label>
            {damageFound ? (
              <div className="rms-field-grid" style={{ marginTop: 10 }}>
                <Field label={t("checkinFlow.whatSDamaged", "What's damaged")}>
                  <textarea className="rms-textarea" value={damageNotes} onChange={(e) => setDamageNotes(e.target.value)} />
                </Field>
                <Field label={`Charge (${operator.currency})`} help={t("checkinFlow.addedToTheInvoice", "Added to the invoice and taken from the deposit first.")}>
                  <input className="rms-input" type="number" min="0" step="0.01" value={damageCharge} onChange={(e) => setDamageCharge(e.target.value)} />
                </Field>
              </div>
            ) : null}
            {damageFound ? (
              <Banner tone="warn">
                {t("checkin.toMaintenance", "This {item} will go to maintenance rather than", {
                        item: tLower("assetSingular", "asset"),
                      })}
                back to available.
              </Banner>
            ) : null}
          </section>

          <section className="rms-card rms-card-pad">
            <h2 className="rms-section-title">
              {t("checkoutFlow.signature", "Signature")}
              {workflow.signatureRequiredAtCheckin ? "" : ` ${t("common.optional", "(optional)")}`}
            </h2>
            <SignaturePad onChange={setSignature} />
          </section>
        </div>

        <aside style={{ position: "sticky", top: 84 }}>
          <div className="rms-card rms-card-pad">
            <h3 className="rms-section-title">{t("checkinFlow.closingSummary", "Closing summary")}</h3>

            {overagePreview.length ? (
              <div style={{ marginBottom: 10 }}>
                {overagePreview.map((o) => (
                  <div key={o.metric.key} style={{ fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid var(--rms-border)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>{o.metric.label}</span>
                      <strong>{o.over > 0 ? formatMoney(o.cents) : "included"}</strong>
                    </div>
                    <div style={{ color: "var(--rms-muted)" }}>
                      {o.used.toLocaleString(getLocale())} {o.metric.unit} used · {o.included.toLocaleString(getLocale())} included
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            <Row label={t("checkinFlow.usageOverage", "Usage overage")} value={formatMoney(overageTotal)} />
            {damageFound ? <Row label={t("checkinFlow.damage", "Damage")} value={formatMoney(damageCents)} /> : null}
            {agreement.deposit_cents ? (
              <Row
                label={t("deposit", "Deposit")}
                value={
                  damageCents >= agreement.deposit_cents
                    ? "forfeited"
                    : damageCents > 0
                    ? "partly held"
                    : `${formatMoney(agreement.deposit_cents)} released`
                }
              />
            ) : null}

            <button
              type="button"
              className="rms-btn rms-btn--primary"
              style={{ width: "100%", marginTop: 14 }}
              disabled={!canClose || busy}
              onClick={handleClose}
            >
              {busy ? t("checkin.closing", "Closing…") : t("checkin.closeAndInvoice", "Close & invoice")}
            </button>
            <p className="rms-help" style={{ marginTop: 8 }}>
              {t("checkinFlow.closesTheAgreementAnd", "Closes the agreement and raises the invoice.")}
            </p>
          </div>
        </aside>
      </div>
    </>

      {signing ? (
        <PinModal
          title={t("pin.signReturn", "Sign this return")}
          explain={t("pin.signReturnHelp", "Your PIN records who took this back.")}
          onCancel={() => setSigning(false)}
          onResolved={close}
        />
      ) : null}
    </>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", fontSize: 13.5 }}>
      <span style={{ color: "var(--rms-muted)" }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}

export default CheckinFlow;
